import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const StudentAttendance = ({ onNavigate, userInfo }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const [activeTab, setActiveTab] = useState('subject-wise');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedRange, setSelectedRange] = useState('7-days');

  const [attendanceData, setAttendanceData] = useState({
    overall: 0,
    present: 0,
    absent: 0,
    total: 0,
    subjectWise: [],
    dateWise: [],
    monthlyStats: {}
  });

  // Sample months for filter
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch detailed attendance data
  useEffect(() => {
    const fetchAttendanceData = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');

        if (!token) {
          setError('Authentication token not found');
          setLoading(false);
          return;
        }

        // Fetch attendance data from API
        const response = await fetch(`${API_LINK}/api/student/attendance`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success && result.data) {
          // Process the attendance data
          const processedData = processAttendanceData(result.data);
          setAttendanceData(processedData);
        } else {
          // Use sample data if API fails
          setAttendanceData(generateSampleAttendanceData());
        }

      } catch (error) {
        console.error('Error fetching attendance data:', error);
        // Use sample data on error
        setAttendanceData(generateSampleAttendanceData());
      } finally {
        setLoading(false);
      }
    };

    fetchAttendanceData();
  }, []);

  // Process attendance data from API
  const processAttendanceData = (apiData) => {
    // Group records by subject
    const subjectGroups = {};
    const dateWiseRecords = [];
    const monthlyStats = {};

    apiData.recentRecords?.forEach(record => {
      const subject = record.subject || 'Unknown Subject';
      const date = new Date(record.date);
      const monthYear = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

      // Subject-wise grouping
      if (!subjectGroups[subject]) {
        subjectGroups[subject] = {
          subject: subject,
          total: 0,
          present: 0,
          absent: 0,
          percentage: 0
        };
      }

      subjectGroups[subject].total++;
      if (record.status === 'present') {
        subjectGroups[subject].present++;
      } else {
        subjectGroups[subject].absent++;
      }

      // Calculate percentage
      subjectGroups[subject].percentage = Math.round(
        (subjectGroups[subject].present / subjectGroups[subject].total) * 100
      );

      // Date-wise records
      dateWiseRecords.push({
        date: record.date,
        subject: subject,
        status: record.status,
        day: date.toLocaleDateString('en-US', { weekday: 'long' })
      });

      // Monthly stats
      if (!monthlyStats[monthYear]) {
        monthlyStats[monthYear] = { present: 0, absent: 0, total: 0 };
      }
      monthlyStats[monthYear].total++;
      if (record.status === 'present') {
        monthlyStats[monthYear].present++;
      } else {
        monthlyStats[monthYear].absent++;
      }
    });

    return {
      overall: apiData.overall || 0,
      present: apiData.present || 0,
      absent: apiData.absent || 0,
      total: apiData.total || 0,
      subjectWise: Object.values(subjectGroups),
      dateWise: dateWiseRecords.sort((a, b) => new Date(b.date) - new Date(a.date)),
      monthlyStats: monthlyStats
    };
  };

  // Generate sample attendance data
  const generateSampleAttendanceData = () => {
    const subjects = [
      'Computer Programming', 'Data Structures', 'Database Management',
      'Operating Systems', 'Computer Networks', 'Software Engineering',
      'Mathematics', 'Physics', 'English Communication'
    ];

    // Generate date-wise records for the last 365 days
    const dateWiseData = [];
    const today = new Date();
    let presentCount = 0;
    let totalCount = 0;
    const subjectGroups = {};

    for (let i = 0; i < 365; i++) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);

      // Skip weekends for a realistic academic schedule
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        // Schedule 1 to 3 classes per day
        const numClasses = Math.floor(Math.random() * 3) + 1;
        for (let c = 0; c < numClasses; c++) {
          const subject = subjects[(Math.floor(Math.random() * subjects.length) + c) % subjects.length];
          const status = Math.random() > 0.15 ? 'present' : 'absent'; // ~85% attendance rate
          const dateStr = date.toISOString().split('T')[0];

          dateWiseData.push({
            date: dateStr,
            subject: subject,
            status: status,
            day: date.toLocaleDateString('en-US', { weekday: 'long' })
          });

          totalCount++;
          if (status === 'present') presentCount++;

          if (!subjectGroups[subject]) {
            subjectGroups[subject] = {
              subject: subject,
              total: 0,
              present: 0,
              absent: 0,
              percentage: 0
            };
          }
          subjectGroups[subject].total++;
          if (status === 'present') {
            subjectGroups[subject].present++;
          } else {
            subjectGroups[subject].absent++;
          }
        }
      }
    }

    // Calculate subject-wise percentages
    Object.keys(subjectGroups).forEach(sub => {
      const s = subjectGroups[sub];
      s.percentage = Math.round((s.present / s.total) * 100);
    });

    return {
      overall: Math.round((presentCount / totalCount) * 100),
      present: presentCount,
      absent: totalCount - presentCount,
      total: totalCount,
      subjectWise: Object.values(subjectGroups),
      dateWise: dateWiseData.sort((a, b) => new Date(b.date) - new Date(a.date)),
      monthlyStats: {}
    };
  };

  // Handle back navigation
  const handleBackNavigation = () => {
    if (onNavigate) {
      onNavigate('student-dashboard');
    }
  };

  // Filter functions
  const getFilteredSubjects = () => {
    return attendanceData.subjectWise.filter(subject =>
      subject.subject.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const getFilteredDateRecords = () => {
    let filtered = attendanceData.dateWise;

    if (searchTerm) {
      filtered = filtered.filter(record =>
        record.subject.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedMonth) {
      const monthIndex = months.indexOf(selectedMonth);
      filtered = filtered.filter(record => {
        const recordDate = new Date(record.date);
        return recordDate.getMonth() === monthIndex;
      });
    }

    if (selectedSubject) {
      filtered = filtered.filter(record => record.subject === selectedSubject);
    }

    return filtered;
  };

  // Get unique subjects for filter
  const getUniqueSubjects = () => {
    return [...new Set(attendanceData.dateWise.map(record => record.subject))];
  };

  // Group and calculate date-wise statistics including every single day in the range
  const generateRangeReport = () => {
    let rangeDays = 7;
    if (selectedRange === '30-days') rangeDays = 30;
    else if (selectedRange === '6-months') rangeDays = 180;
    else if (selectedRange === '1-year') rangeDays = 365;

    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - rangeDays + 1);

    // Sort all records oldest to newest for cumulative calculation
    const allRecordsSorted = [...attendanceData.dateWise].sort((a, b) => new Date(a.date) - new Date(b.date));

    // Initialize cumulative counters by processing records older than startDate
    let cumulativePresent = 0;
    let cumulativeTotal = 0;

    const startDateStr = startDate.toISOString().split('T')[0];

    allRecordsSorted.forEach(record => {
      const recordDateStr = record.date.split('T')[0];
      if (recordDateStr < startDateStr) {
        cumulativeTotal++;
        if (record.status === 'present') {
          cumulativePresent++;
        }
      }
    });

    // Generate all dates in the range, from oldest to newest
    const reportData = [];
    const tempDate = new Date(startDate);

    while (tempDate <= today) {
      const dateStr = tempDate.toISOString().split('T')[0];
      const dayOfWeek = tempDate.toLocaleDateString('en-US', { weekday: 'long' });

      // Find records for this date
      const dayRecords = allRecordsSorted.filter(r => r.date.split('T')[0] === dateStr);

      let dailyStatus = '';
      let dailyPercentage = null;

      if (dayRecords.length > 0) {
        const total = dayRecords.length;
        const present = dayRecords.filter(r => r.status === 'present').length;
        const absent = total - present;

        dailyPercentage = Math.round((present / total) * 100);
        cumulativeTotal += total;
        cumulativePresent += present;

        dailyStatus = present === total ? 'present' : (absent === total ? 'absent' : 'partial');
      } else {
        const isWeekend = tempDate.getDay() === 0 || tempDate.getDay() === 6;
        dailyStatus = isWeekend ? 'weekend' : 'noclass';
      }

      const cumulativePercentage = cumulativeTotal > 0
        ? Math.round((cumulativePresent / cumulativeTotal) * 100)
        : 100;

      reportData.push({
        date: dateStr,
        day: dayOfWeek,
        status: dailyStatus,
        dailyPercentage: dailyPercentage,
        cumulativePercentage: cumulativePercentage,
        rawRecords: dayRecords
      });

      tempDate.setDate(tempDate.getDate() + 1);
    }

    return reportData.reverse(); // Display newest first
  };

  const getRangeStats = (reportData) => {
    let totalClasses = 0;
    let presentClasses = 0;

    reportData.forEach(row => {
      if (row.rawRecords && row.rawRecords.length > 0) {
        totalClasses += row.rawRecords.length;
        presentClasses += row.rawRecords.filter(r => r.status === 'present').length;
      }
    });

    const percentage = totalClasses > 0 ? Math.round((presentClasses / totalClasses) * 100) : 0;
    return {
      percentage,
      total: totalClasses,
      present: presentClasses,
      absent: totalClasses - presentClasses
    };
  };

  // Format date timezone-safely
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const cleanDateStr = dateString.split('T')[0];
    const [year, month, day] = cleanDateStr.split('-');
    if (!year || !month || !day) return dateString;
    const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day} ${monthsShort[parseInt(month, 10) - 1]} ${year}`;
  };

  // Get attendance status color
  const getStatusColor = (status) => {
    return status === 'present'
      ? { background: '#e8f5e9', color: '#2e7d32', border: '1px solid #c8e6c9' }
      : { background: '#ffebee', color: '#c62828', border: '1px solid #ffcdd2' };
  };

  // Get percentage color
  const getPercentageColor = (percentage) => {
    if (percentage >= 90) return '#2e7d32';
    if (percentage >= 75) return '#689f38';
    if (percentage >= 60) return '#f57c00';
    return '#d32f2f';
  };

  if (loading) {
    return (
      <Layout role="student" onNavigate={onNavigate} activePage="student-attendance" pageTitle="View Full Attendance">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', flexDirection: 'column', gap: '1rem' }}>
          <div style={{ width: '50px', height: '50px', border: '4px solid #e3f2fd', borderTop: '4px solid #238548', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
          <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Attendance Data...</p>
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
        </div>
      </Layout>
    );
  }

  return (
    <Layout role="student"
      onNavigate={onNavigate}
      activePage="student-attendance"
      pageTitle="View Full Attendance"
    >
      {/* Header */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: isMobile ? '10px' : '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        padding: isMobile ? '1rem' : '2rem',
        marginBottom: isMobile ? '1rem' : '2rem'
      }}>
        <div style={{ marginBottom: isMobile ? '1rem' : '1.5rem' }}>
          <h1 style={{
            margin: 0,
            color: '#333',
            fontSize: isMobile ? '1.2rem' : '2rem',
            fontWeight: 'bold'
          }}>
            📊 Attendance Details
          </h1>
          <p style={{
            margin: '0.3rem 0 0 0',
            color: '#666',
            fontSize: isMobile ? '0.8rem' : '1.1rem'
          }}>
            Comprehensive view of your attendance records
          </p>
        </div>

        {/* Overall Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: isMobile ? '0.75rem' : '1.5rem'
        }}>
          <div style={{
            textAlign: 'center',
            padding: isMobile ? '0.8rem 0.5rem' : '1.5rem',
            backgroundColor: '#e8f5e9',
            borderRadius: isMobile ? '8px' : '10px',
            border: '2px solid #c8e6c9'
          }}>
            <div style={{ fontSize: isMobile ? '1.3rem' : '2.5rem', fontWeight: 'bold', color: '#2e7d32' }}>
              {attendanceData.overall}%
            </div>
            <div style={{ color: '#2e7d32', fontWeight: 500, fontSize: isMobile ? '0.65rem' : '1rem' }}>Overall Attendance</div>
          </div>

          <div style={{
            textAlign: 'center',
            padding: isMobile ? '0.8rem 0.5rem' : '1.5rem',
            backgroundColor: '#f3e5f5',
            borderRadius: isMobile ? '8px' : '10px',
            border: '2px solid #e1bee7'
          }}>
            <div style={{ fontSize: isMobile ? '1.3rem' : '2.5rem', fontWeight: 'bold', color: '#7b1fa2' }}>
              {attendanceData.present}
            </div>
            <div style={{ color: '#7b1fa2', fontWeight: 500, fontSize: isMobile ? '0.65rem' : '1rem' }}>Present Days</div>
          </div>

          <div style={{
            textAlign: 'center',
            padding: isMobile ? '0.8rem 0.5rem' : '1.5rem',
            backgroundColor: '#ffebee',
            borderRadius: isMobile ? '8px' : '10px',
            border: '2px solid #ffcdd2'
          }}>
            <div style={{ fontSize: isMobile ? '1.3rem' : '2.5rem', fontWeight: 'bold', color: '#c62828' }}>
              {attendanceData.absent}
            </div>
            <div style={{ color: '#c62828', fontWeight: 500, fontSize: isMobile ? '0.65rem' : '1rem' }}>Absent Days</div>
          </div>

          <div style={{
            textAlign: 'center',
            padding: isMobile ? '0.8rem 0.5rem' : '1.5rem',
            backgroundColor: '#e3f2fd',
            borderRadius: isMobile ? '8px' : '10px',
            border: '2px solid #bbdefb'
          }}>
            <div style={{ fontSize: isMobile ? '1.3rem' : '2.5rem', fontWeight: 'bold', color: '#1976d2' }}>
              {attendanceData.total}
            </div>
            <div style={{ color: '#1976d2', fontWeight: 500, fontSize: isMobile ? '0.65rem' : '1rem' }}>Total Classes</div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: isMobile ? '10px' : '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: isMobile ? '1rem' : '2rem',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e0e0e0'
        }}>
          <button
            onClick={() => setActiveTab('subject-wise')}
            style={{
              flex: 1,
              padding: isMobile ? '0.8rem 0.5rem' : '1.5rem',
              border: 'none',
              backgroundColor: activeTab === 'subject-wise' ? '#238548' : 'transparent',
              color: activeTab === 'subject-wise' ? 'white' : '#666',
              fontSize: isMobile ? '0.8rem' : '1.1rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            📚 {isMobile ? 'Subject-wise' : 'Subject-wise Attendance'}
          </button>
          <button
            onClick={() => setActiveTab('date-wise')}
            style={{
              flex: 1,
              padding: isMobile ? '0.8rem 0.5rem' : '1.5rem',
              border: 'none',
              backgroundColor: activeTab === 'date-wise' ? '#238548' : 'transparent',
              color: activeTab === 'date-wise' ? 'white' : '#666',
              fontSize: isMobile ? '0.8rem' : '1.1rem',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
          >
            📅 {isMobile ? 'Date-wise' : 'Date-wise Records'}
          </button>
        </div>

        {/* Filters */}
        <div style={{
          padding: isMobile ? '0.8rem' : '1.5rem',
          backgroundColor: '#f8f9fa',
          display: 'flex',
          gap: isMobile ? '0.5rem' : '1rem',
          flexWrap: 'wrap',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center'
        }}>
          <input
            type="text"
            placeholder="Search subjects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: isMobile ? '0.6rem' : '0.75rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: isMobile ? '0.85rem' : '1rem',
              minWidth: isMobile ? 'unset' : '200px',
              width: isMobile ? '100%' : 'auto'
            }}
          />

          {activeTab === 'date-wise' && (
            <>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                style={{
                  padding: isMobile ? '0.6rem' : '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: isMobile ? '0.85rem' : '1rem',
                  minWidth: isMobile ? 'unset' : '120px',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                <option value="">All Months</option>
                {months.map(month => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>

              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                style={{
                  padding: isMobile ? '0.6rem' : '0.75rem',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: isMobile ? '0.85rem' : '1rem',
                  minWidth: isMobile ? 'unset' : '200px',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                <option value="">All Subjects</option>
                {getUniqueSubjects().map(subject => (
                  <option key={subject} value={subject}>{subject}</option>
                ))}
              </select>
            </>
          )}
        </div>

        {/* Content Area */}
        <div style={{ padding: isMobile ? '0.8rem' : '2rem' }}>
          {activeTab === 'subject-wise' && (
            <div>
              <h3 style={{
                margin: isMobile ? '0 0 0.8rem 0' : '0 0 1.5rem 0',
                color: '#333',
                fontSize: isMobile ? '1rem' : '1.4rem',
                fontWeight: 600
              }}>
                Subject-wise Attendance Statistics
              </h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(350px, 1fr))',
                gap: isMobile ? '0.75rem' : '1.5rem'
              }}>
                {getFilteredSubjects().map((subject, index) => (
                  <div key={index} style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e0e0e0',
                    borderRadius: isMobile ? '8px' : '10px',
                    padding: isMobile ? '0.8rem' : '1.5rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s ease'
                  }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                    }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: isMobile ? '0.5rem' : '1rem'
                    }}>
                      <h4 style={{
                        margin: 0,
                        color: '#333',
                        fontSize: isMobile ? '0.9rem' : '1.2rem',
                        fontWeight: 600
                      }}>
                        {subject.subject}
                      </h4>
                      <span style={{
                        fontSize: isMobile ? '1.1rem' : '1.5rem',
                        fontWeight: 'bold',
                        color: getPercentageColor(subject.percentage)
                      }}>
                        {subject.percentage}%
                      </span>
                    </div>

                    {/* Progress Bar */}
                    <div style={{
                      width: '100%',
                      height: isMobile ? '6px' : '8px',
                      backgroundColor: '#e0e0e0',
                      borderRadius: '4px',
                      marginBottom: isMobile ? '0.5rem' : '1rem',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${subject.percentage}%`,
                        height: '100%',
                        backgroundColor: getPercentageColor(subject.percentage),
                        borderRadius: '4px',
                        transition: 'width 0.3s ease'
                      }}></div>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      fontSize: isMobile ? '0.75rem' : '0.95rem',
                      color: '#666'
                    }}>
                      <span>Present: <strong style={{ color: '#2e7d32' }}>{subject.present}</strong></span>
                      <span>Absent: <strong style={{ color: '#c62828' }}>{subject.absent}</strong></span>
                      <span>Total: <strong style={{ color: '#333' }}>{subject.total}</strong></span>
                    </div>
                  </div>
                ))}
              </div>

              {getFilteredSubjects().length === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: '#666',
                  fontSize: '1.1rem'
                }}>
                  No subjects found matching your search criteria.
                </div>
              )}
            </div>
          )}

          {activeTab === 'date-wise' && (() => {
            const rangeReportData = generateRangeReport();
            const rangeStats = getRangeStats(rangeReportData);
            return (
              <div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1.5rem',
                  flexWrap: 'wrap',
                  gap: '1rem'
                }}>
                  <h3 style={{
                    margin: 0,
                    color: '#333',
                    fontSize: isMobile ? '1.1rem' : '1.4rem',
                    fontWeight: 600
                  }}>
                    Date-wise Attendance Records
                  </h3>

                  {/* Range Selector Buttons */}
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    flexWrap: 'wrap'
                  }}>
                    {[
                      { value: '7-days', label: '7 Days' },
                      { value: '30-days', label: '30 Days' },
                      { value: '6-months', label: '6 Months' },
                      { value: '1-year', label: '1 Year' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setSelectedRange(opt.value)}
                        style={{
                          padding: isMobile ? '0.4rem 0.8rem' : '0.5rem 1.2rem',
                          borderRadius: '20px',
                          border: selectedRange === opt.value ? 'none' : '1px solid #ccc',
                          backgroundColor: selectedRange === opt.value ? '#238548' : '#ffffff',
                          color: selectedRange === opt.value ? '#ffffff' : '#555',
                          fontWeight: '600',
                          fontSize: isMobile ? '0.75rem' : '0.9rem',
                          cursor: 'pointer',
                          boxShadow: selectedRange === opt.value ? '0 2px 8px rgba(35,133,72,0.25)' : 'none',
                          transition: 'all 0.2s ease'
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Range Statistics Summary Card */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                  gap: '1rem',
                  marginBottom: '1.5rem',
                  backgroundColor: '#f8f9fa',
                  padding: '1rem',
                  borderRadius: '10px',
                  border: '1px solid #e0e0e0'
                }}>
                  <div style={{ textAlign: 'center', borderRight: isMobile ? 'none' : '1px solid #dee2e6', padding: '0.25rem' }}>
                    <div style={{ fontSize: isMobile ? '1.2rem' : '1.8rem', fontWeight: 'bold', color: getPercentageColor(rangeStats.percentage) }}>
                      {rangeStats.percentage}%
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 500, marginTop: '0.2rem' }}>Range Attendance</div>
                  </div>
                  <div style={{ textAlign: 'center', borderRight: isMobile ? 'none' : '1px solid #dee2e6', padding: '0.25rem' }}>
                    <div style={{ fontSize: isMobile ? '1.2rem' : '1.8rem', fontWeight: 'bold', color: '#2e7d32' }}>
                      {rangeStats.present}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 500, marginTop: '0.2rem' }}>Present Classes</div>
                  </div>
                  <div style={{ textAlign: 'center', borderRight: isMobile ? 'none' : '1px solid #dee2e6', padding: '0.25rem' }}>
                    <div style={{ fontSize: isMobile ? '1.2rem' : '1.8rem', fontWeight: 'bold', color: '#c62828' }}>
                      {rangeStats.absent}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 500, marginTop: '0.2rem' }}>Absent Classes</div>
                  </div>
                  <div style={{ textAlign: 'center', padding: '0.25rem' }}>
                    <div style={{ fontSize: isMobile ? '1.2rem' : '1.8rem', fontWeight: 'bold', color: '#1976d2' }}>
                      {rangeStats.total}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 500, marginTop: '0.2rem' }}>Total Classes</div>
                  </div>
                </div>

                <div style={{
                  backgroundColor: '#ffffff',
                  borderRadius: isMobile ? '8px' : '10px',
                  overflow: 'hidden',
                  border: '1px solid #e0e0e0',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                }}>
                  {/* Table Header */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1.5fr 2fr 1fr 1fr' : '1.5fr 1fr 2.5fr 1fr 1.2fr',
                    gap: isMobile ? '0.3rem' : '1rem',
                    padding: isMobile ? '0.8rem 0.6rem' : '1.2rem 1.5rem',
                    backgroundColor: '#f8f9fa',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.75rem' : '0.95rem',
                    color: '#333',
                    borderBottom: '2px solid #e0e0e0'
                  }}>
                    <div>Date</div>
                    {!isMobile && <div>Day</div>}
                    <div>Classes & Status</div>
                    <div style={{ textAlign: 'center' }}>Daily %</div>
                    <div style={{ textAlign: 'center' }}>Cumulative %</div>
                  </div>

                  {/* Table Body */}
                  <div style={{ maxHeight: isMobile ? '400px' : '500px', overflowY: 'auto' }}>
                    {rangeReportData.map((row, index) => (
                      <div key={index} style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1.5fr 2fr 1fr 1fr' : '1.5fr 1fr 2.5fr 1fr 1.2fr',
                        gap: isMobile ? '0.3rem' : '1rem',
                        padding: isMobile ? '0.8rem 0.6rem' : '1rem 1.5rem',
                        borderBottom: '1px solid #f0f0f0',
                        backgroundColor: row.status === 'weekend' ? '#fafafa' : '#ffffff',
                        transition: 'all 0.2s ease',
                        alignItems: 'center'
                      }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f1f5f9';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = row.status === 'weekend' ? '#fafafa' : 'transparent';
                        }}>
                        {/* Date */}
                        <div style={{ color: '#333', fontWeight: 600, fontSize: isMobile ? '0.7rem' : '0.95rem' }}>
                          {formatDate(row.date)}
                          {isMobile && (
                            <div style={{ fontSize: '0.6rem', color: '#777', fontWeight: 'normal', marginTop: '0.15rem' }}>
                              {row.day.substring(0, 3)}
                            </div>
                          )}
                        </div>

                        {/* Day (Desktop only) */}
                        {!isMobile && (
                          <div style={{ color: '#555', fontSize: '0.9rem' }}>
                            {row.day}
                          </div>
                        )}

                        {/* Classes & Status */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                          {row.rawRecords && row.rawRecords.length > 0 ? (
                            row.rawRecords.map((rec, rIdx) => (
                              <span key={rIdx} style={{
                                display: 'inline-block',
                                padding: '0.15rem 0.4rem',
                                borderRadius: '4px',
                                fontSize: isMobile ? '0.6rem' : '0.75rem',
                                fontWeight: 600,
                                backgroundColor: rec.status === 'present' ? '#e8f5e9' : '#ffebee',
                                color: rec.status === 'present' ? '#2e7d32' : '#c62828',
                                border: rec.status === 'present' ? '1px solid #c8e6c9' : '1px solid #ffcdd2'
                              }}>
                                {rec.subject}: {rec.status === 'present' ? 'P' : 'A'}
                              </span>
                            ))
                          ) : (
                            <span style={{
                              color: row.status === 'weekend' ? '#aaa' : '#888',
                              fontStyle: 'italic',
                              fontSize: isMobile ? '0.65rem' : '0.85rem'
                            }}>
                              {row.status === 'weekend' ? '🏖️ Weekend' : '⏳ No classes'}
                            </span>
                          )}
                        </div>

                        {/* Daily % */}
                        <div style={{
                          textAlign: 'center',
                          fontWeight: 600,
                          fontSize: isMobile ? '0.7rem' : '0.95rem',
                          color: row.dailyPercentage !== null ? getPercentageColor(row.dailyPercentage) : '#aaa'
                        }}>
                          {row.dailyPercentage !== null ? `${row.dailyPercentage}%` : '—'}
                        </div>

                        {/* Cumulative % */}
                        <div style={{
                          textAlign: 'center',
                          fontWeight: 700,
                          fontSize: isMobile ? '0.7rem' : '0.95rem',
                          color: getPercentageColor(row.cumulativePercentage)
                        }}>
                          {row.cumulativePercentage}%
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {rangeReportData.length === 0 && (
                  <div style={{
                    textAlign: 'center',
                    padding: '3rem',
                    color: '#666',
                    fontSize: '1.1rem'
                  }}>
                    No attendance records found matching this timeframe.
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Additional Stats */}
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: isMobile ? '10px' : '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        padding: isMobile ? '1rem' : '2rem'
      }}>
        <h3 style={{
          margin: isMobile ? '0 0 0.8rem 0' : '0 0 1.5rem 0',
          color: '#333',
          fontSize: isMobile ? '1rem' : '1.4rem',
          fontWeight: 600
        }}>
          📈 Quick Insights
        </h3>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: isMobile ? '0.75rem' : '1.5rem'
        }}>
          <div style={{
            padding: isMobile ? '0.8rem' : '1.5rem',
            backgroundColor: '#f8f9fa',
            borderRadius: isMobile ? '8px' : '10px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ fontSize: isMobile ? '0.85rem' : '1.1rem', fontWeight: 600, color: '#333', marginBottom: '0.3rem' }}>
              Attendance Status
            </div>
            <div style={{ fontSize: isMobile ? '0.8rem' : '1rem', color: '#666' }}>
              {attendanceData.overall >= 75
                ? '✅ Meeting requirement (75%+)'
                : '⚠️ Below minimum requirement'
              }
            </div>
          </div>

          <div style={{
            padding: isMobile ? '0.8rem' : '1.5rem',
            backgroundColor: '#f8f9fa',
            borderRadius: isMobile ? '8px' : '10px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ fontSize: isMobile ? '0.85rem' : '1.1rem', fontWeight: 600, color: '#333', marginBottom: '0.3rem' }}>
              Best Subject
            </div>
            <div style={{ fontSize: isMobile ? '0.8rem' : '1rem', color: '#666' }}>
              {attendanceData.subjectWise.length > 0
                ? attendanceData.subjectWise.reduce((max, subject) =>
                  subject.percentage > max.percentage ? subject : max
                ).subject
                : 'N/A'
              }
            </div>
          </div>

          <div style={{
            padding: isMobile ? '0.8rem' : '1.5rem',
            backgroundColor: '#f8f9fa',
            borderRadius: isMobile ? '8px' : '10px',
            border: '1px solid #e0e0e0'
          }}>
            <div style={{ fontSize: isMobile ? '0.85rem' : '1.1rem', fontWeight: 600, color: '#333', marginBottom: '0.3rem' }}>
              Total Subjects
            </div>
            <div style={{ fontSize: isMobile ? '0.8rem' : '1rem', color: '#666' }}>
              {attendanceData.subjectWise.length} subjects tracked
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default StudentAttendance;